import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, ToggleLeftIcon, ToggleRightIcon, ArrowUpIcon, ArrowDownIcon, FilterIcon, XIcon, TagIcon } from 'lucide-react';
import { Tag } from './TagsManagement';
import { StatusBadge } from '../../StatusBadge';
type SortField = 'name' | 'tagType' | 'active';
type SortDirection = 'asc' | 'desc';
interface TagsListProps {
  globalTags: Tag[];
  orgUnitTags: Tag[];
  onAdd: () => void;
  onEdit: (tag: Tag) => void;
  onToggleActive: (id: string) => void;
  onDelete: (id: string) => void;
  locale: string;
}
export function TagsList({
  globalTags,
  orgUnitTags,
  onAdd,
  onEdit,
  onToggleActive,
  onDelete,
  locale
}: TagsListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    tagType: '',
    active: ''
  });
  const allTags = [...globalTags, ...orgUnitTags];
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const resetFilters = () => {
    setFilters({
      tagType: '',
      active: ''
    });
    setSearchTerm('');
  };
  // Get unique tag types for filters
  const uniqueTagTypes = Array.from(new Set(allTags.filter(tag => tag.tagTypeName).map(tag => tag.tagTypeName)));
  // Apply filters and sorting to a tag list
  const filterAndSortTags = (tags: Tag[]) => {
    const filtered = tags.filter(tag => {
      // Search term filter
      const searchMatch = tag.name.toLowerCase().includes(searchTerm.toLowerCase()) || tag.description && tag.description.toLowerCase().includes(searchTerm.toLowerCase()) || tag.tagTypeName && tag.tagTypeName.toLowerCase().includes(searchTerm.toLowerCase());
      // Tag type filter
      const tagTypeMatch = !filters.tagType || tag.tagTypeName === filters.tagType;
      // Active status filter
      const activeMatch = filters.active === '' || filters.active === 'active' && tag.isActive || filters.active === 'inactive' && !tag.isActive;
      return searchMatch && tagTypeMatch && activeMatch;
    });
    // Apply sorting
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'tagType':
          const tagTypeA = a.tagTypeName || '';
          const tagTypeB = b.tagTypeName || '';
          comparison = tagTypeA.localeCompare(tagTypeB);
          break;
        case 'active':
          comparison = Number(a.isActive) - Number(b.isActive);
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };
  const filteredGlobalTags = filterAndSortTags(globalTags);
  const filteredOrgUnitTags = filterAndSortTags(orgUnitTags);
  // Render tag with its color
  const renderTagBadge = (tag: Tag) => {
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{
      backgroundColor: `${tag.colourHex}20`,
      color: tag.colourHex,
      border: `1px solid ${tag.colourHex}`
    }} data-id="element-20268">
        <TagIcon className="w-3 h-3 mr-1" data-id="element-20269" />
        {tag.name}
      </span>;
  };
  const renderTagTable = (tags: Tag[], showOrgUnit: boolean = false) => {
    return <table className="min-w-full divide-y divide-gray-200" data-id="element-20270">
        <thead className="bg-gray-50" data-id="element-20271">
          <tr data-id="element-20272">
            {showOrgUnit && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20273">
                Organisation Unit
              </th>}
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')} data-id="element-20274">
              <div className="flex items-center space-x-1" data-id="element-20275">
                <span data-id="element-20276">Tag</span>
                {sortField === 'name' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-20277" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-20278" />)}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('tagType')} data-id="element-20279">
              <div className="flex items-center space-x-1" data-id="element-20280">
                <span data-id="element-20281">Tag Type</span>
                {sortField === 'tagType' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-20282" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-20283" />)}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20284">
              Date Range
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('active')} data-id="element-20285">
              <div className="flex items-center space-x-1" data-id="element-20286">
                <span data-id="element-20287">Status</span>
                {sortField === 'active' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-20288" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-20289" />)}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20290">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-20291">
          {tags.length === 0 ? <tr data-id="element-20292">
              <td colSpan={showOrgUnit ? 6 : 5} className="px-6 py-12 text-center text-sm text-gray-500" data-id="element-20293">
                No tags found.
              </td>
            </tr> : tags.map(tag => <tr key={tag.id} className="hover:bg-gray-50" data-id="element-20294">
                {showOrgUnit && <td className="px-6 py-4" data-id="element-20295">
                    <div className="text-sm font-medium text-gray-900" data-id="element-20296">
                      {tag.orgUnitName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-20297">
                      {tag.orgUnitPath}
                    </div>
                  </td>}
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-20298">
                  <div className="flex flex-col" data-id="element-20299">
                    <div className="mb-1" data-id="element-20300">{renderTagBadge(tag)}</div>
                    {tag.description && <span className="text-xs text-gray-500 truncate max-w-xs" data-id="element-20301">
                        {tag.description}
                      </span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-20302">
                  {tag.tagTypeName || <span className="text-gray-400 italic" data-id="element-20303">None</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-20304">
                  {tag.activeFrom ? <span data-id="element-20305">
                      {new Date(tag.activeFrom).toLocaleDateString(locale)}
                      {tag.activeTo ? <span data-id="element-20306">
                          {' '}
                          to {new Date(tag.activeTo).toLocaleDateString(locale)}
                        </span> : <span className="text-gray-400 italic" data-id="element-20307"> - Ongoing</span>}
                    </span> : <span className="text-gray-400 italic" data-id="element-20308">
                      No date restriction
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-20309">
                  <StatusBadge status={tag.isActive ? 'success' : 'error'} text={tag.isActive ? 'Active' : 'Inactive'} data-id="element-20310" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-20311">
                  <div className="flex justify-end space-x-2" data-id="element-20312">
                    <button onClick={() => onEdit(tag)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-20313">
                      <EditIcon className="h-4 w-4" data-id="element-20314" />
                    </button>
                    <button onClick={() => onToggleActive(tag.id)} className={`${tag.isActive ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`} title={tag.isActive ? 'Deactivate' : 'Activate'} data-id="element-20315">
                      {tag.isActive ? <ToggleRightIcon className="h-4 w-4" data-id="element-20316" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-20317" />}
                    </button>
                    <button onClick={() => onDelete(tag.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-20318">
                      <TrashIcon className="h-4 w-4" data-id="element-20319" />
                    </button>
                  </div>
                </td>
              </tr>)}
        </tbody>
      </table>;
  };
  return <div className="space-y-6" data-id="element-20320">
      {/* Search and Filters */}
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-20321">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-20322">
          <div className="flex-1 max-w-md" data-id="element-20323">
            <div className="relative rounded-md shadow-sm" data-id="element-20324">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-20325">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-20326" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search tags..." data-id="element-20327" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-20328">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20329">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-20330" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-20331">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={onAdd} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20332">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-20333" />
              Add Tag
            </button>
          </div>
        </div>

        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-20334">
            {filters.tagType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-20335">
                Type: {filters.tagType}
                <button type="button" onClick={() => setFilters({
            ...filters,
            tagType: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-20336">
                  <XIcon className="h-3 w-3" data-id="element-20337" />
                </button>
              </span>}
            {filters.active && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-20338">
                Status: {filters.active === 'active' ? 'Active' : 'Inactive'}
                <button type="button" onClick={() => setFilters({
            ...filters,
            active: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-20339">
                  <XIcon className="h-3 w-3" data-id="element-20340" />
                </button>
              </span>}
            <button type="button" onClick={resetFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20341">
              Clear All
            </button>
          </div>}

        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-20342">
            <div data-id="element-20343">
              <label htmlFor="tagType" className="block text-sm font-medium text-gray-700" data-id="element-20344">
                Tag Type
              </label>
              <select id="tagType" name="tagType" value={filters.tagType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20345">
                <option value="" data-id="element-20346">All Types</option>
                <option value={null} data-id="element-20347">No Type</option>
                {uniqueTagTypes.map(tagType => <option key={tagType} value={tagType} data-id="element-20348">
                    {tagType}
                  </option>)}
              </select>
            </div>
            <div data-id="element-20349">
              <label htmlFor="active" className="block text-sm font-medium text-gray-700" data-id="element-20350">
                Status
              </label>
              <select id="active" name="active" value={filters.active} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20351">
                <option value="" data-id="element-20352">All Statuses</option>
                <option value="active" data-id="element-20353">Active</option>
                <option value="inactive" data-id="element-20354">Inactive</option>
              </select>
            </div>
          </div>}
      </div>

      {/* Global Tags */}
      <div className="bg-white shadow rounded-lg" data-id="element-20355">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-20356">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20357">
            Global Tags
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20358">
            Global tags available to all organisation units
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-20359">
          {renderTagTable(filteredGlobalTags, false)}
        </div>
      </div>

      {/* OrgUnit Tags */}
      <div className="bg-white shadow rounded-lg" data-id="element-20360">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-20361">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20362">
            Organisation Unit Tags
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20363">
            Tags specific to individual organisation units
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-20364">
          {renderTagTable(filteredOrgUnitTags, true)}
        </div>
      </div>
    </div>;
}